import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchUsers,
  fetchUserDetail,
  createUser,
  updateUser,
  deleteUser,
  assignUserToTeam,
  removeUserFromTeam,
  fetchAllTeams,
} from "../lib/api-client";

function AdminUsersPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  // Create Form state
  const [createForm, setCreateForm] = useState({ email: "", name: "", password: "", globalRole: "FormEditor" });

  const usersQuery = useQuery({
    queryKey: ["admin-users", page, search],
    queryFn: () => fetchUsers({ page, pageSize: 10, search: search || undefined }),
  });

  const createUserMutation = useMutation({
    mutationFn: async () => {
      if (!createForm.email || !createForm.password) throw new Error("Email and password are required");
      return createUser({
        email: createForm.email,
        name: createForm.name || null,
        password: createForm.password,
        globalRole: createForm.globalRole,
      });
    },
    onSuccess() {
      setCreateForm({ email: "", name: "", password: "", globalRole: "FormEditor" });
      setShowCreateDialog(false);
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setToast({ type: "success", message: "User created successfully." });
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to create user.",
      });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      return deleteUser(userId);
    },
    onSuccess() {
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setToast({ type: "success", message: "User deleted successfully." });
    },
    onError(error: unknown) {
      setToast({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to delete user.",
      });
    },
  });

  const users = usersQuery.data?.users ?? [];
  const totalUsers = usersQuery.data?.total ?? 0;
  const totalPages = Math.ceil(totalUsers / 10);

  const handleEditClick = (userId: string) => {
    setSelectedUserId(userId);
    setShowManageDialog(true);
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", background: "var(--color-bg)" }}>
      <header
        style={{
          padding: "1.5rem 2rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid var(--color-border)",
          background: "var(--color-surface)",
        }}
      >
        <div>
          <h1 style={{ fontSize: "1.5rem", margin: 0, fontWeight: 600 }}>User Management</h1>
          <p style={{ margin: "0.25rem 0 0 0", color: "var(--color-text-muted)" }}>
            Manage system access, roles, and team memberships.
          </p>
        </div>
        <button
          type="button"
          className="button primary"
          onClick={() => setShowCreateDialog(true)}
        >
          Create User
        </button>
      </header>

      <main style={{ padding: "2rem", flex: 1, maxWidth: "1200px", margin: "0 auto", width: "100%" }}>
        <div className="surface" style={{ padding: "1.5rem", borderRadius: "8px", boxShadow: "var(--shadow-sm)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1.5rem" }}>
            <div style={{ position: "relative", width: "300px" }}>
              <input
                type="text"
                placeholder="Search users..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{
                  width: "100%",
                  padding: "0.6rem 1rem",
                  borderRadius: "6px",
                  border: "1px solid var(--color-border)",
                  fontSize: "0.9rem"
                }}
              />
            </div>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--color-border)", textAlign: "left", color: "var(--color-text-muted)" }}>
                <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>User</th>
                <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Global Role</th>
                <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Status</th>
                <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Teams</th>
                <th style={{ padding: "0.75rem 1rem", fontWeight: 500 }}>Last Login</th>
                <th style={{ padding: "0.75rem 1rem", fontWeight: 500, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {usersQuery.isLoading ? (
                <tr>
                  <td colSpan={6} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                    Loading users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
                    No users found matching your search.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} style={{ borderBottom: "1px solid var(--color-border-light)", transition: "background 0.2s" }}>
                    <td style={{ padding: "0.75rem 1rem" }}>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontWeight: 500 }}>{user.name || "No Name"}</span>
                        <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>{user.email}</span>
                      </div>
                    </td>
                    <td style={{ padding: "0.75rem 1rem" }}>
                      <RoleBadge role={user.globalRole || "None"} />
                    </td>
                    <td style={{ padding: "0.75rem 1rem" }}>
                      <StatusBadge status={user.status} />
                    </td>
                    <td style={{ padding: "0.75rem 1rem" }}>
                      {user.teamIds.length === 0 ? (
                        <span style={{ color: "var(--color-text-muted)" }}>-</span>
                      ) : (
                        <span style={{ background: "var(--color-bg-soft)", padding: "2px 8px", borderRadius: "12px", fontSize: "0.8rem" }}>
                          {user.teamIds.length} Teams
                        </span>
                      )}
                    </td>
                    <td style={{ padding: "0.75rem 1rem", color: "var(--color-text-muted)" }}>
                      {user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : "Never"}
                    </td>
                    <td style={{ padding: "0.75rem 1rem", textAlign: "right" }}>
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                        <button
                          className="button ghost"
                          style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem" }}
                          onClick={() => handleEditClick(user.id)}
                        >
                          Edit
                        </button>
                        <button
                          className="button ghost"
                          style={{ padding: "0.4rem 0.8rem", fontSize: "0.85rem", color: "var(--color-critical)" }}
                          onClick={() => {
                            if (confirm(`Are you sure you want to delete ${user.email}?`)) {
                              deleteUserMutation.mutate(user.id);
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1.5rem", borderTop: "1px solid var(--color-border)", paddingTop: "1rem" }}>
            <span style={{ color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
              Page {page} of {totalPages || 1}
            </span>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                className="button ghost"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </button>
              <button
                className="button ghost"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* Create User Dialog */}
      {showCreateDialog && (
        <Dialog
          title="Create New User"
          onClose={() => setShowCreateDialog(false)}
          onSubmit={() => createUserMutation.mutateAsync()}
          isLoading={createUserMutation.isPending}
          width="450px"
        >
          <div className="form-group">
            <label className="field">
              <span>Email</span>
              <input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="user@example.com"
                required
                className="input"
              />
            </label>
            <label className="field">
              <span>Name</span>
              <input
                type="text"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                placeholder="John Doe"
                className="input"
              />
            </label>
            <label className="field">
              <span>Password</span>
              <input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="••••••••"
                required
                className="input"
              />
            </label>
            <label className="field">
              <span>Global Role</span>
              <select
                value={createForm.globalRole}
                onChange={(e) => setCreateForm({ ...createForm, globalRole: e.target.value })}
                className="select"
              >
                <option value="FormViewer">Form Viewer</option>
                <option value="FormEditor">Form Editor</option>
                <option value="TeamAdmin">Team Admin</option>
                <option value="DomainManager">Domain Manager</option>
                <option value="SuperAdmin">Super Admin</option>
              </select>
            </label>
          </div>
        </Dialog>
      )}

      {/* User Management Dialog (Edit + Teams) */}
      {showManageDialog && selectedUserId && (
        <UserManagementDialog
          userId={selectedUserId}
          onClose={() => {
            setShowManageDialog(false);
            setSelectedUserId(null);
          }}
          setToast={setToast}
        />
      )}

      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: "fixed",
            bottom: "2rem",
            right: "2rem",
            background: toast.type === "success" ? "#10B981" : "#EF4444",
            color: "white",
            padding: "0.8rem 1.5rem",
            borderRadius: "6px",
            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            zIndex: 2000,
            fontWeight: 500,
            animation: "slideIn 0.3s ease-out",
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  const styles: Record<string, { bg: string; color: string }> = {
    SuperAdmin: { bg: "#EDE9FE", color: "#6D28D9" }, // Purple
    TeamAdmin: { bg: "#DBEAFE", color: "#1E40AF" }, // Blue
    FormEditor: { bg: "#FEF3C7", color: "#92400E" }, // Amber
    FormViewer: { bg: "#F3F4F6", color: "#374151" }, // Gray
    DomainManager: { bg: "#DCFCE7", color: "#166534" }, // Green
    None: { bg: "#F3F4F6", color: "#9CA3AF" },
  };

  const style = styles[role] || styles.None;

  return (
    <span style={{
      background: style.bg,
      color: style.color,
      padding: "2px 8px",
      borderRadius: "12px",
      fontSize: "0.75rem",
      fontWeight: 600
    }}>
      {role}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; color: string; dot: string }> = {
    active: { bg: "#D1FAE5", color: "#065F46", dot: "#059669" },
    invited: { bg: "#FEF3C7", color: "#92400E", dot: "#D97706" },
    disabled: { bg: "#FEE2E2", color: "#991B1B", dot: "#DC2626" },
    suspended: { bg: "#F3F4F6", color: "#374151", dot: "#6B7280" },
  };

  const style = styles[status] || styles.suspended;

  return (
    <span style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      fontSize: "0.8rem",
      color: style.color
    }}>
      <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: style.dot }} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function UserManagementDialog({
  userId,
  onClose,
  setToast
}: {
  userId: string;
  onClose: () => void;
  setToast: (toast: { type: "success" | "error"; message: string } | null) => void;
}) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"profile" | "teams">("profile");

  const { data: userDetail, isLoading } = useQuery({
    queryKey: ["user-detail", userId],
    queryFn: () => fetchUserDetail(userId),
  });

  const selectedUser = userDetail?.user;

  // Edit User Logic
  const [editForm, setEditForm] = useState<{ name: string; email: string; globalRole: string; status: string; password?: string }>({
    name: "", email: "", globalRole: "", status: ""
  });

  // Initialize form when data loads
  useMemo(() => {
    if (selectedUser) {
      setEditForm({
        name: selectedUser.name || "",
        email: selectedUser.email,
        globalRole: selectedUser.globalRole || "",
        status: selectedUser.status,
      });
    }
  }, [selectedUser]);

  const updateUserMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {};
      if (editForm.email !== selectedUser?.email) payload.email = editForm.email;
      if (editForm.name !== selectedUser?.name) payload.name = editForm.name || null;
      if (editForm.globalRole !== selectedUser?.globalRole) payload.globalRole = editForm.globalRole || undefined;
      if (editForm.status !== selectedUser?.status) payload.status = editForm.status;
      if (editForm.password) payload.password = editForm.password;

      return updateUser(userId, payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      void queryClient.invalidateQueries({ queryKey: ["user-detail", userId] });
      setToast({ type: "success", message: "Profile updated successfully." });
    },
    onError: (err: Error) => setToast({ type: "error", message: err.message || "Failed to update profile." })
  });

  // Team Logic
  const [teamForm, setTeamForm] = useState({ teamId: "", role: "FormEditor" });
  const teamsQuery = useQuery({ queryKey: ["all-teams"], queryFn: fetchAllTeams });

  const assignTeamMutation = useMutation({
    mutationFn: () => assignUserToTeam(userId, teamForm.teamId, teamForm.role),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["user-detail", userId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] }); // Update team count
      setTeamForm(prev => ({ ...prev, teamId: "" }));
      setToast({ type: "success", message: "Team assigned." });
    },
    onError: (err: Error) => setToast({ type: "error", message: err.message || "Failed assign team." })
  });

  const removeTeamMutation = useMutation({
    mutationFn: (teamId: string) => removeUserFromTeam(userId, teamId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["user-detail", userId] });
      void queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      setToast({ type: "success", message: "Removed from team." });
    },
    onError: (err: Error) => setToast({ type: "error", message: err.message || "Failed removal." })
  });

  if (isLoading || !selectedUser) {
    return (
      <Dialog title="Loading..." onClose={onClose} onSubmit={() => { }} isLoading={false} width="600px">
        <div style={{ padding: "2rem", textAlign: "center" }}>Loading user details...</div>
      </Dialog>
    );
  }

  return (
    <Dialog
      title={`Manage User: ${selectedUser.email}`}
      onClose={onClose}
      onSubmit={() => { if (activeTab === "profile") updateUserMutation.mutate() }}
      isLoading={updateUserMutation.isPending}
      width="600px"
      hideFooter={activeTab === "teams"}
    >
      <div style={{ borderBottom: "1px solid var(--color-border)", marginBottom: "1.5rem", display: "flex", gap: "1.5rem" }}>
        <button
          type="button"
          onClick={() => setActiveTab("profile")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "profile" ? "2px solid var(--color-primary)" : "2px solid transparent",
            color: activeTab === "profile" ? "var(--color-primary)" : "var(--color-text-muted)",
            padding: "0.5rem 0",
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          Profile
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("teams")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "teams" ? "2px solid var(--color-primary)" : "2px solid transparent",
            color: activeTab === "teams" ? "var(--color-primary)" : "var(--color-text-muted)",
            padding: "0.5rem 0",
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          Team Memberships
        </button>
      </div>

      {activeTab === "profile" && (
        <div className="form-group" style={{ display: "grid", gap: "1rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <label className="field">
              <span>Name</span>
              <input
                className="input"
                value={editForm.name}
                onChange={e => setEditForm({ ...editForm, name: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Email</span>
              <input
                className="input"
                value={editForm.email}
                onChange={e => setEditForm({ ...editForm, email: e.target.value })}
              />
            </label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <label className="field">
              <span>Global Role</span>
              <select
                className="select"
                value={editForm.globalRole}
                onChange={e => setEditForm({ ...editForm, globalRole: e.target.value })}
              >
                <option value="">No Global Role</option>
                <option value="FormViewer">Form Viewer</option>
                <option value="FormEditor">Form Editor</option>
                <option value="TeamAdmin">Team Admin</option>
                <option value="DomainManager">Domain Manager</option>
                <option value="SuperAdmin">Super Admin</option>
              </select>
            </label>
            <label className="field">
              <span>Status</span>
              <select
                className="select"
                value={editForm.status}
                onChange={e => setEditForm({ ...editForm, status: e.target.value })}
              >
                <option value="invited">Invited</option>
                <option value="active">Active</option>
                <option value="disabled">Disabled</option>
                <option value="suspended">Suspended</option>
              </select>
            </label>
          </div>

          <label className="field">
            <span>Change Password (Optional)</span>
            <input
              className="input"
              type="password"
              placeholder="New password..."
              value={editForm.password || ""}
              onChange={e => setEditForm({ ...editForm, password: e.target.value })}
            />
          </label>
        </div>
      )}

      {activeTab === "teams" && (
        <div>
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.5rem", alignItems: "flex-end" }}>
            <label className="field" style={{ flex: 1 }}>
              <span style={{ fontSize: "0.85rem" }}>Add to Team</span>
              <select
                className="select"
                value={teamForm.teamId}
                onChange={e => setTeamForm({ ...teamForm, teamId: e.target.value })}
              >
                <option value="">Select Team...</option>
                {teamsQuery.data?.teams.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </label>
            <label className="field" style={{ width: "150px" }}>
              <span style={{ fontSize: "0.85rem" }}>Role</span>
              <select
                className="select"
                value={teamForm.role}
                onChange={e => setTeamForm({ ...teamForm, role: e.target.value })}
              >
                <option value="FormViewer">Viewer</option>
                <option value="FormEditor">Editor</option>
                <option value="TeamAdmin">Admin</option>
                <option value="DomainManager">Domain Manager</option>
              </select>
            </label>
            <button
              className="button primary"
              onClick={() => assignTeamMutation.mutate()}
              disabled={!teamForm.teamId || assignTeamMutation.isPending}
            >
              Add
            </button>
          </div>

          <h4 style={{ fontSize: "0.9rem", color: "var(--color-text-muted)", marginBottom: "0.5rem" }}>Current Memberships</h4>
          <div style={{ background: "var(--color-bg-soft)", borderRadius: "6px", overflow: "hidden" }}>
            {userDetail.teamMemberships.length === 0 ? (
              <div style={{ padding: "1rem", textAlign: "center", fontStyle: "italic", fontSize: "0.9rem" }}>No team memberships</div>
            ) : (
              userDetail.teamMemberships.map(tm => (
                <div key={tm.teamId} style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "0.75rem 1rem",
                  borderBottom: "1px solid var(--color-border-light)"
                }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: "0.9rem" }}>{tm.teamName}</div>
                    <div style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>{tm.role}</div>
                  </div>
                  <button
                    className="button ghost"
                    style={{ color: "var(--color-critical)", padding: "0.25rem 0.5rem", fontSize: "0.8rem" }}
                    onClick={() => removeTeamMutation.mutate(tm.teamId)}
                  >
                    Remove
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}

function Dialog({
  title,
  onClose,
  onSubmit,
  isLoading,
  children,
  width = "400px",
  hideFooter = false
}: {
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  isLoading: boolean;
  children: React.ReactNode;
  width?: string;
  hideFooter?: boolean;
}): JSX.Element {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        backdropFilter: "blur(2px)"
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--color-surface)",
          borderRadius: "8px",
          padding: "0",
          width: width,
          maxWidth: "95vw",
          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
          animation: "scaleIn 0.2s ease-out",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "1.5rem", borderBottom: "1px solid var(--color-border)" }}>
          <h2 style={{ fontSize: "1.25rem", fontWeight: 600, margin: 0 }}>{title}</h2>
        </div>

        <div style={{ padding: "1.5rem", overflowY: "auto" }}>
          <form
            id="dialog-form"
            onSubmit={(e) => {
              e.preventDefault();
              void onSubmit();
            }}
          >
            {children}
          </form>
        </div>

        {!hideFooter && (
          <div style={{ padding: "1rem 1.5rem", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: "0.75rem", background: "var(--color-bg-soft)", borderBottomLeftRadius: "8px", borderBottomRightRadius: "8px" }}>
            <button type="button" className="button ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" form="dialog-form" className="button primary" disabled={isLoading}>
              {isLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUsersPage;
